import { captureEvent } from '@sentry/react';
import { useEffect, useState } from 'react';
import type { StatusPageSummaryResponse } from 'typings/StatusPageScheduledMaintenance';

// Public Incident.io widget API, no API key required:
// https://docs.incident.io/status-pages/api#widget-api
const STATUS_PAGE_SUMMARY_URL =
  'https://status.apollographql.com/api/v1/summary';

async function fetchSummary(signal: AbortSignal) {
  const response = await fetch(STATUS_PAGE_SUMMARY_URL, { signal });
  if (!response.ok) {
    throw new Error(`Status page request failed: ${response.status}`);
  }

  const data: StatusPageSummaryResponse = await response.json();
  if (
    !Array.isArray(data?.ongoing_incidents) ||
    !Array.isArray(data?.in_progress_maintenances) ||
    !Array.isArray(data?.scheduled_maintenances)
  ) {
    throw new Error('Invalid status page summary');
  }

  return data;
}

/**
 * Fetches the status page summary on mount and then on an interval while the
 * tab is visible. Returns `undefined` until the first successful response.
 */
export function useStatusPageSummary() {
  const [summary, setSummary] = useState<StatusPageSummaryResponse>();

  useEffect(() => {
    let controller: AbortController | undefined;

    const updateSummary = async () => {
      controller?.abort();
      const currentController = new AbortController();
      controller = currentController;
      try {
        const data = await fetchSummary(currentController.signal);
        if (!currentController.signal.aborted) {
          setSummary(data);
        }
      } catch {
        if (!currentController.signal.aborted) {
          captureEvent(new Error('Failed to fetch status page summary'));
        }
      }
    };

    updateSummary();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        updateSummary();
      }
    }, 60_000); // poll so incident updates reach users who keep Studio open

    return () => {
      clearInterval(interval);
      controller?.abort();
    };
  }, []);

  return summary;
}
